// Análisis de ventas — Portal Admin VentasLuga (F3 · pista Retail 🔵).
// Mockup: `surekeep/mockups/backoffice/portal-admin-vl-analisis.html`.
//
// Conectada a los endpoints reales: 29a (`/backoffice/sales/analytics`, wizard) y 29b
// (`/backoffice/inventory/rotation`, catalog), más `/backoffice/sellers/kpis` para la tienda de cada
// vendedor. El layout es calcado del mockup: grid de 2 columnas con tres cards a ancho completo.
//
//   1. Mix por tienda × financiera            (ancho completo)
//   2. Cohorte tienda vs periodo anterior  ·  3. Rechazos SK que sí compraron con otra financiera
//   4. Top SKUs                            ·  5. Vendedores top vs bottom
//   6. Rotación de inventario por tienda      (ancho completo)
//   7. Vendedores · mes en curso vs mes anterior (ancho completo)
//   8. Inventario estancado y capital atado   (ancho completo, lente del catálogo)
//
// ── Lo que el mock pinta y el backend NO da ──────────────────────────────────────────────────────
// · **Comisión del mes**: el motor de comisiones no está desplegado. `s/d` y tooltip — inventar un
//   número de dinero es el peor error posible de esta pantalla.
// · **Rechazos SK**: cruza el scoring de la SOFOM con las ventas del piso. Pista 🟣, sin repos.
import { useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { ErrorState, Skeleton } from '@/kit';
import { ScreenBand, ScreenBody } from '@/shared/patterns/ScreenBand';
import { ScreenErrorBoundary } from '@/shared/patterns/ScreenErrorBoundary';
import { userMessageOf } from '@/api/errors';
import { cn } from '@/lib/cn';
import type { Financier } from '@/api/services/retail-org/backoffice';
import {
    ANALYTICS_RANGE_KEYS,
    type AnalyticsRangeKey,
    type SellerRankRow,
} from '@/api/services/retail-wizard/backoffice';
import type { InventoryLensPeriod } from '@/api/services/retail-catalog/backoffice';
import {
    useAnalisis,
    useAnalisisRotation,
    useAnalisisSellerStores,
    useAnalisisStagnantInventory,
} from './api/useAnalisis';
import { StagnantInventoryPanel } from './components/StagnantInventoryPanel';
import {
    changeState,
    financiersPresent,
    formatChange,
    formatShare,
    formatUnits,
    moneyShort,
    formatWindow,
    hiddenRotationCount,
    namedRotation,
    pick,
    pivotMatrix,
    plural,
    sellerExtremes,
    shortSellerLabel,
    shortStoreLabel,
    rankedSkus,
    skuLabel,
    sortCohort,
    storeNamesFromRanges,
    zonesFromRanges,
} from './lib/analisisMetrics';

/** Etiquetas cortas de la matriz, como el mockup: SK · PJ · CY · LP · IV · Ctd. */
const FINANCIER_ORDER: Financier[] = ['SUREKEEP', 'PAYJOY', 'CREDIYA', 'LESPAGO', 'INNOVA', 'CASH'];
const FINANCIER_SHORT: Record<Financier, string> = {
    SUREKEEP: 'SK',
    PAYJOY: 'PJ',
    CREDIYA: 'CY',
    LESPAGO: 'LP',
    INNOVA: 'IV',
    CASH: 'Ctd',
};

/**
 * 🔴 El de 30 días NO dice «Mes en curso», aunque el mock lo diga.
 *
 * La ventana del silo es MÓVIL: hoy va del 9 de julio al 7 de agosto. Un Admin VL que lee «Mes en
 * curso · 172 piezas» reporta eso como el número de agosto, y arranca en julio. La ventana real se
 * pinta al lado, en la misma barra, con `formatWindow`.
 */
const RANGE_LABEL: Record<AnalyticsRangeKey, string> = {
    today: 'Hoy',
    '7d': '7 días',
    '30d': '30 días',
};
const RANGE_DAYS: Record<AnalyticsRangeKey, number> = { today: 1, '7d': 7, '30d': 30 };

/** Periodo de la lente para cada rango; la lente no mide un solo día, así que «Hoy» usa 7 días. */
const RANGE_LENS_PERIOD: Record<AnalyticsRangeKey, InventoryLensPeriod> = {
    today: '7d',
    '7d': '7d',
    '30d': '30d',
};

/** Cuántos vendedores pinta el mes-contra-mes. El mock corta en 20 y la lista puede ser enorme. */
const MOM_ROW_LIMIT = 20;

// ── Piezas del mockup ────────────────────────────────────────────────────────────────────────────

/**
 * Card del grid. `wide` la estira a las dos columnas, como `.analisis-card.wide`.
 *
 * El título es un `<h2>` y no un `<span>`: con siete tableros y cero encabezados, un lector de
 * pantalla no tenía por dónde navegar la página.
 */
function Card({
    title,
    meta,
    wide = false,
    children,
}: {
    title: string;
    meta?: React.ReactNode;
    wide?: boolean;
    children: React.ReactNode;
}) {
    return (
        <section
            className={cn(
                'flex min-h-0 flex-col overflow-hidden rounded-md border border-border bg-card',
                wide && 'col-span-2',
            )}
            aria-label={title}
        >
            <div className="flex flex-none items-center justify-between gap-2 border-b border-border bg-muted/30 px-2.5 py-1.5">
                <h2 className="text-[11.5px] font-semibold uppercase tracking-[0.04em]">{title}</h2>
                {meta ? <span className="text-[10.5px] text-muted-foreground">{meta}</span> : null}
            </div>
            <div className="min-h-0 flex-1 overflow-auto py-2">{children}</div>
        </section>
    );
}

/**
 * Lo que va dentro de una card cuando no hay nada que pintar.
 *
 * Seis de las siete cards quedaban con encabezado y el vacío debajo. Con el pill «Hoy» eso pasa a
 * primera hora de la mañana, en operación normal — y una tabla vacía sin explicación se lee como
 * «se rompió», no como «todavía no hay ventas».
 */
function EmptyCard({ children }: { children: React.ReactNode }) {
    return (
        <p className="p-3 text-center text-[11px] italic leading-relaxed text-muted-foreground">
            {children}
        </p>
    );
}

const TH = 'bg-muted/40 px-2 py-1 text-left text-[9.5px] font-bold uppercase tracking-[0.04em]';
const TD = 'border-b border-border/50 px-2 py-1 text-[10.5px]';
const NUM = 'text-right tabular-nums';

/**
 * `+12%` en chip verde / rojo / gris, o el motivo por el que no hay porcentaje.
 *
 * `changePct` viene EN PUNTOS PORCENTUALES desde el silo (`42` = 42 %), no en fracción: por eso el
 * umbral de «plano» es ±5 y no ±0.05, y `formatChange` NO re-escala.
 */
export function DeltaChip({
    changePct,
    current,
    previous,
}: {
    changePct: number | null;
    current: number;
    previous: number | null;
}) {
    const state = changeState(changePct, current, previous);
    // Base en cero con ventas hoy: hay crecimiento real, solo que no se puede expresar en porcentaje.
    if (state.kind === 'fromZero') {
        return (
            <span
                className="whitespace-nowrap rounded-[3px] bg-success-soft px-1 py-px text-[9.5px] font-bold text-success"
                title="El periodo anterior no tuvo ventas: hay crecimiento, pero ningún porcentaje que calcular"
            >
                ↑ desde 0
            </span>
        );
    }
    // Los dos guiones dicen cosas distintas: sin actividad en ninguno de los dos periodos, o sin base.
    if (state.kind !== 'pct') {
        return (
            <span
                className="text-[9.5px] text-muted-foreground"
                title={
                    state.kind === 'noActivity'
                        ? 'Sin ventas en este periodo ni en el anterior'
                        : 'Sin periodo anterior con qué comparar'
                }
            >
                —
            </span>
        );
    }
    const tone =
        state.pct > 5
            ? 'bg-success-soft text-success'
            : state.pct < -5
              ? 'bg-destructive/15 text-destructive'
              : 'bg-muted text-muted-foreground';
    return (
        <span className={cn('rounded-[3px] px-1 py-px text-[9.5px] font-bold tabular-nums', tone)}>
            {formatChange(state.pct)}
        </span>
    );
}

/** Barra de progreso del mockup (`.bar-wrap` / `.bar-fill`). */
function Bar({ pct, tone = 'ok' }: { pct: number; tone?: 'ok' | 'warn' | 'danger' | 'none' }) {
    // `none` para el valor ausente: una barra verde de ancho 0 se lee como «rota rapidísimo», que es
    // lo contrario de «no tengo con qué calcularlo».
    const fill =
        tone === 'none'
            ? 'bg-muted-foreground/30'
            : tone === 'danger'
              ? 'bg-destructive'
              : tone === 'warn'
                ? 'bg-warning'
                : 'bg-success';
    return (
        <div className="h-2 flex-1 overflow-hidden rounded-[3px] bg-muted">
            <div
                className={cn('h-full rounded-[3px]', fill)}
                style={{ width: `${String(pct)}%` }}
            />
        </div>
    );
}

// ── La pantalla ──────────────────────────────────────────────────────────────────────────────────

export function AnalisisPage() {
    const [range, setRange] = useState<AnalyticsRangeKey>('today');
    const query = useAnalisis();
    const rotationQuery = useAnalisisRotation();
    const sellerStoresQuery = useAnalisisSellerStores();
    const stagnantQuery = useAnalisisStagnantInventory(RANGE_LENS_PERIOD[range]);

    const data = query.data;
    const blocks = data?.ranges[range];
    const days = RANGE_DAYS[range];

    const financiers = useMemo(
        () => (blocks ? financiersPresent(blocks.financierMix, FINANCIER_ORDER) : []),
        [blocks],
    );

    const matrix = useMemo(
        () => (blocks ? pivotMatrix(blocks.financierMix, financiers) : []),
        [blocks, financiers],
    );

    /** La fila «Total red» del mockup: la suma por columna, no un dato aparte. */
    const totals = useMemo(() => {
        const byFinancier = Object.fromEntries(financiers.map((f) => [f, 0])) as Record<
            Financier,
            number
        >;
        let total = 0;
        for (const row of matrix) {
            for (const f of financiers) byFinancier[f] += row.byFinancier[f];
            total += row.total;
        }
        return { byFinancier, total };
    }, [matrix, financiers]);

    /**
     * Zona de cada tienda, para el subtítulo del mockup.
     *
     * 🔴 Sale de los TRES rangos, no del activo. La zona es un atributo del padrón —no cambia porque
     * mires otra ventana de tiempo— pero el único bloque que la trae es `financierMix`, y ahí solo
     * están las tiendas que vendieron en ese rango. Armándolo con el rango activo, Iztapalapa salía
     * «Iztapalapa / —» en Hoy y «Iztapalapa / Zona Centro» en 30 días, con exactamente los mismos
     * números al lado: la etiqueta bailaba sola y parecía un dato inestable.
     *
     * Es el mismo criterio que ya usaban los nombres —que sí mergeaban dos fuentes—; la asimetría
     * entre ambos mapas era el bug.
     */
    const zoneByStore = useMemo(() => zonesFromRanges(data?.ranges ?? {}), [data]);

    const storeBySeller = useMemo(
        () =>
            new Map(
                (sellerStoresQuery.data?.items ?? []).map((s) => [s.sellerId, s.storeName ?? null]),
            ),
        [sellerStoresQuery.data],
    );

    /**
     * «Falló la llamada» y «este vendedor no tiene tienda» son cosas distintas y las dos pintaban el
     * mismo `—`. En mes-contra-mes llegaban a convivir con el `—` de «no hay motor de comisiones»:
     * tres guiones con tres significados en el mismo renglón.
     */
    const sellerStoresFailed = sellerStoresQuery.isError;
    const storeLabelFor = (sellerId: string) =>
        sellerStoresFailed ? '· · ·' : (storeBySeller.get(sellerId) ?? '—');
    const storeTitleFor = (sellerId: string) => {
        if (sellerStoresFailed) return 'No se pudo leer el padrón de vendedores';
        return storeBySeller.get(sellerId) ? undefined : 'Este vendedor no tiene tienda asignada';
    };

    const extremes = useMemo(
        () => (blocks ? sellerExtremes(blocks.sellerRanking, 3) : { top: [], bottom: [] }),
        [blocks],
    );

    // Los nombres salen del mix Y de la cohorte: el mix solo tiene las tiendas de la ventana actual,
    // y esta tabla no depende del rango — con solo el mix se renombraba al cambiar de pill.
    // 🔴 NO depende de `blocks`. Los nombres sí salen de ahí, pero los NÚMEROS son del lambda de
    // inventario: atar la tabla entera a que el análisis de ventas responda la borraba de la pantalla
    // por un dato que solo es la etiqueta. Sin `blocks`, las filas salen con su etiqueta corta.
    // Y como los números de esta tabla NO dependen del rango, los nombres tampoco deben: se juntan
    // de los tres. Con solo el rango activo, una tienda que no vendió hoy perdía su nombre al pasar
    // a «Hoy» y volvía a tenerlo en «30 días», sin que ninguna de sus seis columnas cambiara.
    const rotationRows = useMemo(() => {
        if (!rotationQuery.data) return [];
        return namedRotation(rotationQuery.data.stores, storeNamesFromRanges(data?.ranges ?? {}));
    }, [rotationQuery.data, data]);

    // Cuántas tiendas del endpoint no se pintan, para anunciarlo en el encabezado de la card.
    const hiddenRotationRows = hiddenRotationCount(rotationQuery.data?.stores ?? []);

    const maxRotation = Math.max(1, ...rotationRows.map((r) => r.averageRotationDays ?? 0));
    const topSkus = rankedSkus(blocks?.topSkus ?? []);
    const maxSkuUnits = Math.max(1, ...topSkus.map((s) => s.units));

    /** Renglón de vendedor de «top vs bottom» — numerado, con su tienda debajo. */
    const sellerRow = (seller: SellerRankRow, index: number, isTop: boolean) => (
        <div
            key={seller.sellerRetailUserId}
            className="grid grid-cols-[22px_1fr_50px_70px] items-center gap-1.5 border-b border-border/50 px-2.5 py-1.5 text-[11px]"
        >
            <span
                className={cn('text-right font-bold', isTop ? 'text-success' : 'text-destructive')}
            >
                {index + 1}
            </span>
            <div className="min-w-0">
                <div className="truncate font-medium">
                    {seller.sellerName ?? shortSellerLabel(seller.sellerRetailUserId)}
                </div>
                <div
                    className="truncate text-[9.5px] text-muted-foreground"
                    title={storeTitleFor(seller.sellerRetailUserId)}
                >
                    {storeLabelFor(seller.sellerRetailUserId)}
                </div>
            </div>
            <span className="text-right font-semibold tabular-nums">
                {formatUnits(seller.units)}
            </span>
            <span className="text-right font-semibold tabular-nums">
                {moneyShort(seller.cents)}
            </span>
        </div>
    );

    /**
     * La explicación del asterisco, en un solo lugar porque la usan el `title` de la celda y el
     * `aria-label` del propio asterisco — y tienen que decir lo MISMO.
     *
     * Dice **dónde se corrige**. La versión anterior cerraba con «es un dato a corregir, no un
     * cálculo raro», que deja al operador sabiendo que algo está mal y sin idea de a dónde ir. Y
     * pluraliza de verdad: `1 pieza(s)` era el paréntesis que este repo ya había desterrado del
     * resto de la pantalla.
     */
    const impossibleHint = (count: number) =>
        `${plural(count, 'pieza de esta tienda quedó fuera', 'piezas de esta tienda quedaron fuera')} del promedio: su venta figura ANTES de su ingreso al piso. Se corrige en el alta de IMEIs o cancelando la venta mal capturada.`;

    /**
     * La tabla de Rotación, extraída a una variable a propósito.
     *
     * 🔴 Sus datos vienen de OTRO lambda (29b, inventario) y no dependen del análisis de ventas. Vivía
     * dentro de la rama `else` del error, así que un 500 del 29a la borraba de la pantalla aunque su
     * propio endpoint hubiera respondido 200. Se declara aquí para poder pintarla en los DOS caminos:
     * con la pantalla sana, en su posición del mock; y con el análisis caído, sola pero viva.
     */
    const stagnantCard = (
        <Card
            title="Inventario estancado y capital atado"
            meta={
                range === 'today'
                    ? 'Sin venta en los últimos 7 días · la lente no mide un solo día'
                    : `Sin venta en los últimos ${RANGE_LABEL[range]} · stock de hoy por precio vigente`
            }
            wide
        >
            <StagnantInventoryPanel
                data={stagnantQuery.data}
                isPending={stagnantQuery.isPending}
                error={stagnantQuery.error}
                onRetry={() => {
                    void stagnantQuery.refetch();
                }}
            />
        </Card>
    );

    const rotationCard = (
        <Card
            title="Rotación de inventario por tienda"
            // El backend hace un promedio SIMPLE de `soldAt − receivedAt` sobre las
            // piezas vendidas. El texto anterior («ponderada por stock disponible»)
            // venía del mock y describía una fórmula que nadie calcula.
            // El aviso de piezas descartadas va aquí y no en una columna: es de PÁGINA, no de fila.
            // Una sexta columna casi siempre en cero ensuciaría una tabla que se lee para rankear.
            // 🔴 «Todo el inventario, no el rango» va PRIMERO, y es lo único en negritas.
            //
            // Esta card ignora el selector de rango a propósito —la rotación es la vida útil de la
            // pieza, no un corte de ventana— pero vive debajo de un control que cambia todo lo demás
            // y no dispara un solo request al moverlo. Alguien parado en «Hoy» leía «119d» como si
            // fuera de hoy.
            //
            // El primer intento puso el aviso en medio de una frase de tres cláusulas encadenadas
            // con «·», en gris de 10.5px. Las palabras estaban, pero nadie las leía: en una pantalla
            // con siete cards el subtítulo se escanea, no se lee. Y la señal más fuerte (el pill de
            // rango, resaltado) seguía contradiciendo a la más débil. Adelantarlo y marcarlo es lo
            // mínimo para que compitan de igual a igual.
            meta={
                <>
                    <strong className="font-semibold text-foreground">
                        Todo el inventario, no el rango
                    </strong>
                    {' · Días promedio entre que la pieza entra al piso y se vende'}
                    {(rotationQuery.data?.impossibleDates ?? 0) > 0
                        ? ` · ${plural(rotationQuery.data?.impossibleDates ?? 0, 'pieza quedó fuera', 'piezas quedaron fuera')} del promedio porque su venta figura antes de su ingreso`
                        : ''}
                    {hiddenRotationRows > 0
                        ? ` · ${plural(hiddenRotationRows, 'tienda sin stock ni ventas queda fuera', 'tiendas sin stock ni ventas quedan fuera')} de la tabla`
                        : ''}
                </>
            }
            wide
        >
            {rotationQuery.isError ? (
                // Mismo patrón que el error del 29a tres cards arriba: un fallo no puede verse igual
                // que el estado vacío ni dejar al operador sin cómo reintentar.
                <ErrorState
                    title="No se pudo cargar la rotación de inventario"
                    description={`${userMessageOf(rotationQuery.error)} Los cortes de venta de esta pantalla no dependen del inventario y siguen siendo válidos.`}
                    onRetry={() => {
                        void rotationQuery.refetch();
                    }}
                    className="m-2.5"
                />
            ) : rotationQuery.isPending ? (
                <Skeleton lines={5} />
            ) : rotationRows.length === 0 ? (
                <EmptyCard>Ninguna tienda tiene inventario registrado.</EmptyCard>
            ) : (
                <table className="w-full table-fixed border-collapse text-[10.5px]">
                    <thead>
                        <tr>
                            <th scope="col" className={cn(TH, 'w-[24%]')}>
                                Tienda
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                Stock
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                SKUs con stock
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                Rotación prom.
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                SKUs frescos ≤15d
                            </th>
                            {/* Renombrada: mide «el lote más reciente llegó hace
                                >90d», que es falta de REPOSICIÓN, no de rotación.
                                El header viejo prometía la métrica del mock. */}
                            <th scope="col" className={cn(TH, 'text-right')}>
                                SKUs sin reponer &gt;90d
                            </th>
                            <th scope="col" className={cn(TH, 'w-[120px]')}>
                                Índice
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rotationRows.map((row) => {
                            const value = row.averageRotationDays;
                            const tone =
                                value === null
                                    ? 'none'
                                    : value > 60
                                      ? 'danger'
                                      : value > 30
                                        ? 'warn'
                                        : 'ok';
                            return (
                                <tr key={row.storeId}>
                                    <td className={TD}>
                                        <div className="truncate" title={row.storeName}>
                                            {row.storeName}
                                        </div>
                                        <div className="truncate text-[9px] text-muted-foreground">
                                            {zoneByStore.get(row.storeId) ?? '—'}
                                        </div>
                                    </td>
                                    <td className={cn(TD, NUM)}>{formatUnits(row.totalStock)}</td>
                                    <td className={cn(TD, NUM)}>
                                        {formatUnits(row.skusWithStock)}
                                    </td>
                                    <td
                                        className={cn(TD, NUM, 'font-bold')}
                                        // Un `—` NO es cero: la tienda no ha vendido
                                        // ni una pieza, así que no hay promedio.
                                        title={
                                            value === null
                                                ? 'Sin ventas: no hay con qué calcular el promedio'
                                                : (row.impossibleDates ?? 0) > 0
                                                  ? impossibleHint(row.impossibleDates ?? 0)
                                                  : undefined
                                        }
                                    >
                                        {value === null ? '—' : `${String(value)}d`}
                                        {/* El asterisco marca la tienda a la que hay que ir: el
                                            promedio es real, pero se calculó con menos piezas de
                                            las que esa tienda vendió.

                                            🔴 Lleva su explicación en `aria-label`, no solo en el
                                            `title` de la celda. Un `title` no existe en touch ni con
                                            teclado, y este asterisco no tiene ninguna señal visual de
                                            que sea hoverable: para quien no usa mouse, el dato
                                            simplemente no estaba.

                                            Y tiene que ser ALCANZABLE CON TECLADO. Con solo el
                                            `aria-label` quedaban cubiertos el lector de pantalla y
                                            el mouse, pero no el teclado sin lector: media solución.

                                            Es un `<button>` y no un `<span tabIndex={0}>` porque
                                            eslint tiene razón: poner `tabIndex` en algo no
                                            interactivo mete en el orden de tabulación un elemento
                                            que no responde a Enter ni a Espacio, y quien llega ahí
                                            se queda esperando que pase algo. El botón sí es
                                            interactivo —el navegador le muestra el `title` al
                                            enfocarlo— y el subrayado punteado le da por fin la
                                            señal visual de que hay algo detrás. */}
                                        {(row.impossibleDates ?? 0) > 0 ? (
                                            <button
                                                type="button"
                                                className="ml-0.5 cursor-help align-baseline text-warning underline decoration-dotted underline-offset-2"
                                                aria-label={impossibleHint(
                                                    row.impossibleDates ?? 0,
                                                )}
                                                title={impossibleHint(row.impossibleDates ?? 0)}
                                            >
                                                *
                                            </button>
                                        ) : null}
                                        {/* El promedio sin su denominador no se puede leer: 119d
                                            sobre 27 piezas y 107d sobre 5 no pesan igual. */}
                                        {row.soldUnits > 0 ? (
                                            <div className="text-[9px] font-normal text-muted-foreground">
                                                {`sobre ${plural(row.soldUnits, 'pieza vendida', 'piezas vendidas')}`}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td
                                        className={cn(
                                            TD,
                                            NUM,
                                            'font-semibold',
                                            row.freshSkus > 0
                                                ? 'text-success'
                                                : 'text-muted-foreground',
                                        )}
                                    >
                                        {formatUnits(row.freshSkus)}
                                    </td>
                                    <td
                                        className={cn(
                                            TD,
                                            NUM,
                                            'font-semibold',
                                            row.staleSkus > 0
                                                ? 'text-warning'
                                                : 'text-muted-foreground',
                                        )}
                                    >
                                        {formatUnits(row.staleSkus)}
                                    </td>
                                    <td className={TD}>
                                        <div className="flex items-center gap-1">
                                            <Bar
                                                pct={((value ?? 0) / maxRotation) * 100}
                                                tone={tone}
                                            />
                                            <span className="text-[10px] font-bold tabular-nums">
                                                {value === null ? '—' : `${String(value)}d`}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </Card>
    );

    return (
        <div className="flex h-full min-h-0 flex-col">
            {/* Barra de rango — `.rango-bar` del mockup: etiqueta, pills y la nota a la derecha. */}
            <ScreenBand className="text-[11px]">
                <span className="flex-none text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Rango
                </span>
                {ANALYTICS_RANGE_KEYS.map((key) => (
                    <button
                        key={key}
                        type="button"
                        aria-pressed={range === key}
                        onClick={() => {
                            setRange(key);
                        }}
                        className={cn(
                            'flex-none rounded border px-2.5 py-[3px] text-[11px] transition-colors',
                            range === key
                                ? 'border-primary bg-primary font-medium text-primary-foreground'
                                : 'border-border bg-card hover:bg-accent/50',
                        )}
                    >
                        {RANGE_LABEL[key]}
                    </button>
                ))}
                <div className="flex-1" />
                <span className="flex-none text-[10.5px] text-muted-foreground">
                    {/* `pick` y no `plural`: `plural` antepone el conteo, y con una frase que ya trae
                        su artículo salía «Comparativa contra 7 los días previos». */}
                    Comparativa contra{' '}
                    {pick(days, 'el día previo', `los ${String(days)} días previos`)}
                    {blocks ? ` · ${formatWindow(blocks.from, blocks.to)}` : ''}
                </span>
                {/* Los datos se cachean 5 minutos: sin esto el operador no tiene cómo pedir la foto
                    nueva ni cómo saber que está viendo una vieja. */}
                <button
                    type="button"
                    onClick={() => {
                        void query.refetch();
                        void rotationQuery.refetch();
                        void sellerStoresQuery.refetch();
                    }}
                    disabled={query.isFetching}
                    // 24×24 es el mínimo de WCAG 2.2 AA para un objetivo táctil. Con 22 no pasaba.
                    className="flex size-6 flex-none items-center justify-center rounded border border-border text-muted-foreground transition-colors hover:bg-accent/50 disabled:opacity-50"
                    title="Actualizar los datos"
                    aria-label="Actualizar los datos"
                >
                    <RefreshCw
                        className={cn('size-3', query.isFetching && 'animate-spin')}
                        aria-hidden
                    />
                </button>
                {/* El spinner es puramente visual: sin esto, quien usa lector de pantalla presiona y
                    no se entera de nada. */}
                <span className="sr-only" role="status" aria-live="polite">
                    {query.isFetching ? 'Actualizando los datos…' : ''}
                </span>
            </ScreenBand>

            <ScreenBody>
                {/* El boundary envuelve SOLO el contenido: un dato malformado no debe llevarse el
                    sidebar ni el topbar, que son la salida del operador. `resetKeys` con el rango
                    porque el crash suele venir de UNA combinación de datos — cambiar de pill limpia
                    el error solo, sin recargar. */}
                <ScreenErrorBoundary
                    resetKeys={[range, data]}
                    title="No se pudo mostrar el análisis de ventas"
                >
                    {query.isPending ? (
                        <div className="grid grid-cols-2 gap-2.5">
                            {/* El skeleton respeta los `wide` del layout real: si no, el grid salta al
                            llegar los datos. */}
                            {[true, false, false, false, false, true, true].map((wide, i) => (
                                <div
                                    key={i}
                                    className={cn(
                                        'rounded-md border border-border bg-card p-3',
                                        wide && 'col-span-2',
                                    )}
                                >
                                    <Skeleton lines={5} />
                                </div>
                            ))}
                        </div>
                    ) : query.isError || !data || !blocks ? (
                        /* 🔴 El análisis de ventas cayó, PERO la rotación viene de otro lambda. Antes este
                       early-return se la llevaba por delante aunque su endpoint hubiera respondido
                       200: la pantalla quedaba en blanco con un dato bueno del otro lado. Ahora el
                       error ocupa las dos columnas y la tabla de rotación sigue debajo. */
                        <div className="grid grid-cols-2 gap-2.5">
                            <div className="col-span-2">
                                <ErrorState
                                    title="No se pudieron cargar los cortes de venta"
                                    description={userMessageOf(query.error)}
                                    onRetry={() => {
                                        void query.refetch();
                                    }}
                                />
                            </div>
                            {rotationQuery.data ? rotationCard : null}
                            {stagnantQuery.data ? stagnantCard : null}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2.5">
                            {/* Avisos del silo. Van ARRIBA del grid: cambian cómo se leen TODOS los
                            números de abajo, así que verlos después sería tarde. */}
                            {data.truncatedCurrent || data.truncatedPrevious ? (
                                <div className="col-span-2 flex items-start gap-1.5 rounded-md border border-warning bg-warning/15 px-2.5 py-1.5 text-[10.5px] font-medium text-foreground">
                                    <AlertTriangle
                                        className="mt-px size-3 flex-none text-warning"
                                        aria-hidden
                                    />
                                    <span>
                                        El silo cortó la lectura{' '}
                                        {data.truncatedCurrent && data.truncatedPrevious
                                            ? 'de los dos periodos'
                                            : data.truncatedCurrent
                                              ? 'del periodo actual'
                                              : 'del periodo anterior'}
                                        . Las variaciones contra el periodo previo no son confiables
                                        — no las leas como aproximadas.
                                    </span>
                                </div>
                            ) : null}

                            {/* 1 · Mix por tienda × financiera ─────────────────────────────────── */}
                            <Card
                                title="Mix por tienda × financiera"
                                meta={`Piezas · últimos ${String(days)}d · ${formatUnits(totals.total)} piezas`}
                                wide
                            >
                                {matrix.length === 0 ? (
                                    <EmptyCard>Sin ventas en el periodo.</EmptyCard>
                                ) : (
                                    <table className="w-full table-fixed border-collapse text-[10.5px]">
                                        <thead>
                                            <tr>
                                                <th scope="col" className={cn(TH, 'w-[26%]')}>
                                                    Tienda
                                                </th>
                                                {financiers.map((f) => (
                                                    <th
                                                        key={f}
                                                        scope="col"
                                                        className={cn(TH, 'text-right')}
                                                    >
                                                        {FINANCIER_SHORT[f]}
                                                    </th>
                                                ))}
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Total
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {matrix.map((row) => {
                                                // La celda «hot» es el MÁXIMO de la fila: dónde se apoya
                                                // esa tienda. Con una sola financiera usada no se marca
                                                // nada — un único valor no es un máximo que informe.
                                                const values = financiers.map(
                                                    (f) => row.byFinancier[f],
                                                );
                                                const max = Math.max(...values);
                                                const usadas = values.filter((v) => v > 0).length;
                                                // Tampoco se marca si TODAS empatan: una fila entera en
                                                // verde hace ver a la tienda con el mix más parejo como
                                                // la más concentrada, que es lo contrario de lo que el
                                                // color quiere decir. Móvil Norte llegó a tener 5 de 6.
                                                const enElMaximo = values.filter(
                                                    (v) => v === max,
                                                ).length;
                                                const destacable = usadas > 1 && enElMaximo === 1;
                                                return (
                                                    <tr key={row.storeId}>
                                                        <td className={TD}>
                                                            <div
                                                                className="truncate"
                                                                title={row.storeName}
                                                            >
                                                                {row.storeName}
                                                            </div>
                                                            <div className="truncate text-[9px] text-muted-foreground">
                                                                {zoneByStore.get(row.storeId) ??
                                                                    '—'}
                                                            </div>
                                                        </td>
                                                        {financiers.map((f) => {
                                                            const value = row.byFinancier[f];
                                                            return (
                                                                <td
                                                                    key={f}
                                                                    className={cn(
                                                                        TD,
                                                                        NUM,
                                                                        value === 0 &&
                                                                            'text-muted-foreground',
                                                                        value > 0 &&
                                                                            value === max &&
                                                                            destacable &&
                                                                            'bg-success-soft font-semibold',
                                                                    )}
                                                                >
                                                                    {value > 0
                                                                        ? formatUnits(value)
                                                                        : '—'}
                                                                </td>
                                                            );
                                                        })}
                                                        <td
                                                            className={cn(TD, NUM, 'font-semibold')}
                                                        >
                                                            {formatUnits(row.total)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            <tr className="bg-muted/30 font-bold">
                                                <td className={TD}>Total red</td>
                                                {financiers.map((f) => (
                                                    <td key={f} className={cn(TD, NUM)}>
                                                        {formatUnits(totals.byFinancier[f])}
                                                    </td>
                                                ))}
                                                <td className={cn(TD, NUM)}>
                                                    {formatUnits(totals.total)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                )}
                            </Card>

                            {/* 2 · Cohorte tienda vs periodo anterior ──────────────────────────── */}
                            <Card
                                title="Cohorte tienda vs periodo anterior"
                                meta={`últimos ${String(days)}d vs ${String(days)}d anteriores`}
                            >
                                {blocks.storeCohort.length === 0 ? (
                                    <EmptyCard>
                                        Ninguna tienda vendió en este periodo ni en el anterior.
                                    </EmptyCard>
                                ) : (
                                    <table className="w-full table-fixed border-collapse text-[10.5px]">
                                        <thead>
                                            <tr>
                                                <th scope="col" className={cn(TH, 'w-[34%]')}>
                                                    Tienda
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Piezas act.
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Δ piezas
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Vendido act.
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Δ vendido
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortCohort(blocks.storeCohort).map((row) => {
                                                const name =
                                                    row.storeName ?? shortStoreLabel(row.storeId);
                                                return (
                                                    <tr key={row.storeId}>
                                                        <td
                                                            className={cn(TD, 'truncate')}
                                                            title={name}
                                                        >
                                                            {name}
                                                        </td>
                                                        <td className={cn(TD, NUM)}>
                                                            {formatUnits(row.units)}
                                                        </td>
                                                        <td className={cn(TD, NUM)}>
                                                            <DeltaChip
                                                                changePct={row.unitsChangePct}
                                                                current={row.units}
                                                                previous={row.previousUnits}
                                                            />
                                                        </td>
                                                        <td className={cn(TD, NUM)}>
                                                            {moneyShort(row.cents)}
                                                        </td>
                                                        <td className={cn(TD, NUM)}>
                                                            <DeltaChip
                                                                changePct={row.centsChangePct}
                                                                current={row.cents}
                                                                previous={row.previousCents}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </Card>

                            {/* 3 · Rechazos SK — la card existe aunque el dato no ──────────────── */}
                            <Card
                                title="Rechazos SK que sí compraron con otra financiera"
                                meta="Oportunidad perdida SK"
                            >
                                <EmptyCard>{data.lostOpportunity.reason}</EmptyCard>
                            </Card>

                            {/* 4 · Top SKUs ────────────────────────────────────────────────────── */}
                            <Card
                                title="Top SKUs"
                                meta={
                                    topSkus.length >= 10
                                        ? 'Por piezas vendidas · los 10 primeros'
                                        : 'Por piezas vendidas'
                                }
                            >
                                {topSkus.length === 0 ? (
                                    <EmptyCard>Sin ventas en el periodo.</EmptyCard>
                                ) : (
                                    topSkus.map((row) => (
                                        <div
                                            key={row.sku}
                                            className="grid grid-cols-[1fr_40px_56px_64px] items-center gap-1.5 border-b border-border/50 px-2.5 py-[5px] text-[11px]"
                                        >
                                            <div className="min-w-0">
                                                <div className="truncate font-medium">
                                                    {skuLabel(row)}
                                                </div>
                                                <div className="truncate text-[9.5px] text-muted-foreground">
                                                    {row.sku}
                                                </div>
                                            </div>
                                            <span className="text-right font-semibold tabular-nums">
                                                {formatUnits(row.units)}
                                            </span>
                                            <div className="flex items-center">
                                                <Bar pct={(row.units / maxSkuUnits) * 100} />
                                            </div>
                                            {/* 64 px y `whitespace-nowrap`: con 44 px, `100% SK` se
                                            partía en dos líneas y descuadraba el renglón. */}
                                            <div className="whitespace-nowrap text-right">
                                                <div className="text-[10px] font-bold text-success">
                                                    {formatShare(row.sureKeepSharePct)} SK
                                                </div>
                                                {/* El porcentaje solo no dice nada: «100 % SK» sobre
                                                    una pieza no es lo mismo que sobre dieciocho. */}
                                                <div className="text-[9px] tabular-nums text-muted-foreground">
                                                    {`${formatUnits(row.sureKeepUnits)} de ${formatUnits(row.units)}`}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </Card>

                            {/* 5 · Vendedores top vs bottom ────────────────────────────────────── */}
                            <Card
                                title="Vendedores top vs bottom"
                                // El orden es por PIEZAS (el monto solo desempata) y se DICE: la card
                                // muestra las dos columnas, y quien lee «top» asume dinero. Sin esta
                                // etiqueta, un vendedor con menos piezas y más dinero queda fuera del
                                // top 3 sin explicación visible.
                                meta={`Diferencial de piso · por piezas · últimos ${String(days)}d`}
                            >
                                {extremes.top.length === 0 ? (
                                    <EmptyCard>
                                        Ningún vendedor con actividad en el periodo.
                                    </EmptyCard>
                                ) : (
                                    <>
                                        <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.04em] text-success">
                                            Top 3
                                        </div>
                                        {extremes.top.map((seller, i) =>
                                            sellerRow(seller, i, true),
                                        )}
                                        {/* El rótulo solo si hay filas: con ≤3 vendedores el bottom
                                        queda vacío y antes se pintaba el encabezado huérfano. */}
                                        {extremes.bottom.length > 0 ? (
                                            <>
                                                <div className="mt-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.04em] text-destructive">
                                                    Bottom 3
                                                </div>
                                                {extremes.bottom.map((seller, i) =>
                                                    sellerRow(seller, i, false),
                                                )}
                                            </>
                                        ) : null}
                                    </>
                                )}
                            </Card>

                            {/* 6 · Rotación de inventario por tienda ───────────────────────────── */}
                            {rotationCard}

                            {/* 8 · Inventario estancado y capital atado ──────────────────────────── */}
                            {stagnantCard}

                            {/* 7 · Vendedores mes contra mes ───────────────────────────────────── */}
                            <Card
                                title="Vendedores · mes en curso vs mes anterior"
                                meta={
                                    data.sellerMonthOverMonth.length > MOM_ROW_LIMIT
                                        ? `Ventanas fijas: 30 días vs 30-60 previos · los ${String(MOM_ROW_LIMIT)} primeros de ${String(data.sellerMonthOverMonth.length)}`
                                        : 'Ventanas fijas: últimos 30 días vs 30-60 días previos'
                                }
                                wide
                            >
                                {data.sellerMonthOverMonth.length === 0 ? (
                                    <EmptyCard>
                                        Ningún vendedor con actividad en los últimos 60 días.
                                    </EmptyCard>
                                ) : (
                                    <table className="w-full table-fixed border-collapse text-[10.5px]">
                                        <thead>
                                            <tr>
                                                <th scope="col" className={cn(TH, 'w-[22%]')}>
                                                    Vendedor
                                                </th>
                                                <th scope="col" className={cn(TH, 'w-[18%]')}>
                                                    Tienda
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Piezas mes
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Δ vs anterior
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Vendido mes
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Δ vendido
                                                </th>
                                                <th scope="col" className={cn(TH, 'text-right')}>
                                                    Comisión mes
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* El mock corta en 20 y la implementación pintaba la lista
                                            completa: con la red crecida, esa card se vuelve
                                            infinita. El corte se anuncia en el `meta` de arriba. */}
                                            {data.sellerMonthOverMonth
                                                .slice(0, MOM_ROW_LIMIT)
                                                .map((row) => {
                                                    const name =
                                                        row.sellerName ??
                                                        shortSellerLabel(row.sellerRetailUserId);
                                                    return (
                                                        <tr key={row.sellerRetailUserId}>
                                                            <td
                                                                className={cn(
                                                                    TD,
                                                                    'truncate font-medium',
                                                                )}
                                                                title={name}
                                                            >
                                                                {name}
                                                            </td>
                                                            <td
                                                                className={cn(TD, 'truncate')}
                                                                title={storeTitleFor(
                                                                    row.sellerRetailUserId,
                                                                )}
                                                            >
                                                                {storeLabelFor(
                                                                    row.sellerRetailUserId,
                                                                )}
                                                            </td>
                                                            <td className={cn(TD, NUM)}>
                                                                {formatUnits(row.units)}
                                                            </td>
                                                            <td className={cn(TD, NUM)}>
                                                                <DeltaChip
                                                                    changePct={row.unitsChangePct}
                                                                    current={row.units}
                                                                    previous={row.previousUnits}
                                                                />
                                                            </td>
                                                            <td className={cn(TD, NUM)}>
                                                                {moneyShort(row.cents)}
                                                            </td>
                                                            <td className={cn(TD, NUM)}>
                                                                <DeltaChip
                                                                    changePct={row.centsChangePct}
                                                                    current={row.cents}
                                                                    previous={row.previousCents}
                                                                />
                                                            </td>
                                                            {/* `s/d` y no `—`: en esta misma fila hay guiones
                                                        que significan otras cosas. El motor de
                                                        comisiones no está desplegado, y un número
                                                        inventado aquí es dinero que alguien cobra. */}
                                                            <td
                                                                className={cn(
                                                                    TD,
                                                                    NUM,
                                                                    'text-muted-foreground',
                                                                )}
                                                                title="El motor de comisiones todavía no está desplegado"
                                                            >
                                                                s/d
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                )}
                            </Card>

                            {/* Las derivadas sin financiera van al PIE: es una nota sobre cómo leer la
                            columna Ctd, no una alarma.
                            🔴 El texto anterior decía «fuera del mix» y era FALSO — el silo las manda
                            a `NONE`, el mismo bucket que el contado, así que SÍ están adentro y sí
                            inflan Ctd. El contador existe justo para poder advertirlo. */}
                            {/* 🔴 Este pie ya dijo las dos cosas al revés. Primero afirmaba que las
                            huérfanas quedaban «fuera del mix» cuando estaban dentro; después se
                            corrigió a «incluye», justo cuando el backend las sacó — y volvió a
                            mentir, ahora en el otro sentido, pidiendo restar de un número correcto.
                            Hoy el silo las descarta del mix, y esto explica el hueco que eso deja.
                            El conteo sale de `blocks`, o sea del rango seleccionado: el global de
                            60 días decía «5 ventas» con el pill «Hoy» y cero ese día. */}
                            {blocks.orphanReferrals > 0 ? (
                                <p className="col-span-2 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[10.5px] text-muted-foreground">
                                    <span className="font-semibold text-foreground">
                                        Fuera del mix:
                                    </span>{' '}
                                    {plural(
                                        blocks.orphanReferrals,
                                        'venta derivada a un tercero',
                                        'ventas derivadas a un tercero',
                                    )}{' '}
                                    sin financiera registrada.{' '}
                                    {pick(
                                        blocks.orphanReferrals,
                                        'No es de contado y no se sabe con quién financió',
                                        'No son de contado y no se sabe con quién financiaron',
                                    )}
                                    , así que no {pick(blocks.orphanReferrals, 'está', 'están')} en
                                    ninguna columna. Sí cuentan en el top de modelos y en el ranking
                                    de vendedores, que no dependen de la financiera — por eso «Total
                                    red» suma menos que ellos.
                                </p>
                            ) : null}
                        </div>
                    )}
                </ScreenErrorBoundary>
            </ScreenBody>
        </div>
    );
}
